import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useEarningsLeaderboard, useMyFantasyFams } from "../../../lib/api/fantasy-fams";
import { useMySubscription } from "../../../lib/api/subscription";
import { BankrollTabs, LeaderboardRow } from "./_shared";
import type { LeaderboardBankrollTier } from "../../../lib/types";

// /fantasy/earnings — Fantasy Fams Earnings Showdown leaderboard, one bankroll
// pool at a time. The empty state covers both "no showdown live" and
// "showdown live but no entries yet".
export function EarningsLeaderboardRoute() {
	const [bankroll, setBankroll] = useState<LeaderboardBankrollTier>("10k");
	const leaderboard = useEarningsLeaderboard(bankroll);

	// Free users can only open the $10K pool; $100K / $1M tabs are locked.
	const isPaid = useMySubscription().data?.status === "active";

	const myFams = useMyFantasyFams();
	const myFamSlugs = useMemo(
		() => new Set((myFams.data?.fantasyFams ?? []).map((f) => f.slug)),
		[myFams.data],
	);

	const data = leaderboard.data;

	return (
		<div className="mx-auto max-w-4xl space-y-6 px-4 py-8">
			<header className="space-y-1">
				<Link
					to="/fantasy"
					className="text-xs text-[var(--color-muted)] hover:text-[var(--color-fg)]"
				>
					← Fantasy
				</Link>
				<h1 className="text-2xl font-bold">Fantasy Fams: Earnings Showdown</h1>
				<p className="text-sm text-[var(--color-muted)]">
					Themed week — nail a ticker that reports this week and its allocation
					scores 1.5×. Each bankroll tier competes in its own pool.
				</p>
			</header>

			<BankrollTabs value={bankroll} onChange={setBankroll} isPaid={isPaid} />

			{leaderboard.isLoading && (
				<p className="text-sm text-[var(--color-muted)]">Loading…</p>
			)}
			{leaderboard.isError && (
				<p className="text-sm text-[var(--color-danger)]">
					Could not load leaderboard.{" "}
					{leaderboard.error instanceof Error ? leaderboard.error.message : ""}
				</p>
			)}

			{data && data.entries.length > 0 && (
				<>
					{data.period && (
						<section className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-4">
							<p className="text-xs uppercase tracking-wide text-[var(--color-muted)]">
								Earnings week
							</p>
							<p className="text-lg font-semibold">{data.period}</p>
						</section>
					)}
					<section className="space-y-2">
						{data.entries.map((entry) => (
							<LeaderboardRow
								key={entry.famId}
								entry={entry}
								isYours={!!entry.famSlug && myFamSlugs.has(entry.famSlug)}
								showTickers
							/>
						))}
					</section>
				</>
			)}

			{data?.entries.length === 0 && (
				<section className="space-y-3 rounded-lg border border-dashed border-[var(--color-border)] bg-[var(--color-bg-elev)] p-6 text-center">
					<p className="text-sm font-semibold">No earnings showdown live</p>
					<p className="text-xs text-[var(--color-muted)]">
						Earnings clusters run roughly six to eight weeks each year. Opt
						your Fam into Fantasy Fams Earnings from settings — entries fire
						automatically when the next showdown opens.
					</p>
				</section>
			)}

			<footer className="border-t border-[var(--color-border)] pt-4 text-center text-xs text-[var(--color-muted)]">
				Returns are composite-weighted, simulated paper trades.{" "}
				<a
					href="https://hedgefam.com/disclosures"
					className="underline hover:text-[var(--color-fg)]"
				>
					Disclosures
				</a>
				.
			</footer>
		</div>
	);
}
